<template>
  <div
    :class="appendCls"
    @click.stop="toggleMenu"
    v-clickoutside="handleClose"
  >
    <zm-scrollbar
      class="zm-select-scrollbar"
      :wrap-style="inputWrapStyl">
      <div
        ref="reference"
        class="zm-select-input"
        :style="inputStyl"
        :class="{
          'has-slot': $slots.prefix,
          'zm-select--show-label': value.length && isInlineLabel
        }"
      >
        <label v-if="value.length && isInlineLabel" class="zm-select__label" :for="labelFor">{{ inlineLabelText }}</label>
        <div class="zm-select-prefix" v-if="$slots.prefix">
          <slot name="prefix"></slot>
        </div>
        <input
          ref="referenceInner"
          v-if="!multiple"
          class="zm-select-input__inner"
          autocomplete="off"
          v-model="query"
          type="text"
          v-bind="$attrs"
          :placeholder="currentPlaceholder"
          :disabled="selectDisabled"
          :validate-event="false"
          :aria-controls="listId"
          :aria-owns="listId"
          :aria-required="isRequired"
          :aria-disabled="selectDisabled"
          :aria-label="label"
          :aria-describedby="labelBy"
          role="combobox"
          :aria-expanded="(visible).toString()"
          :aria-activedescendant="visible ? activedescendant: null"
          aria-haspopup="true"
          @compositionstart="handleComposition"
          @compositionupdate="handleComposition"
          @compositionend="handleComposition"
          @keydown="handleKeyDown"
          @input="debouncedQueryChange"
          @focus="handleFocus"
          @blur="handleBlur"
        />
        <div
          class="zm-select__tags"
          v-if="multiple"
          ref="tags"
          :style="{ 'max-width': inputWidth - 25 + 'px' }">
          <span v-if="collapseTags && selected.length">
            <zm-tag
              :closable="!selectDisabled"
              class="zm-tag-fix"
              :size="collapseTagSize"
              :hit="selected[0].hitState"
              :delete-text="t('tag.delete') + ' ' + selected[0].currentLabel"
              type="info"
              @close="deleteTag($event, selected[0])"
              disable-transitions>
              <span class="zm-select__tags-text">{{ selected[0].currentLabel }}
              </span>
            </zm-tag>
            <zm-tag
              class="zm-tag-fix"
              v-if="selected.length > 1"
              :closable="false"
              :size="collapseTagSize"
              type="info"
              disable-transitions>
            <span class="zm-select__tags-text">+ {{ selected.length - 1 }}
            </span>
            </zm-tag>
          </span>
          <span v-if="!collapseTags">
            <zm-tag
              v-for="item in selected"
              :key="getValueKey(item)"
              :delete-text="t('tag.delete') + ' '+ item.currentLabel"
              :closable="!selectDisabled"
              :size="collapseTagSize"
              :hit="item.hitState"
              type="info"
              class="zm-tag-fix"
              @close="deleteTag($event, item)"
              disable-transitions>
              <span class="zm-select__tags-text">{{ item.currentLabel }}</span>
            </zm-tag>
          </span>
          <input
            ref="input"
            type="text"
            v-bind="$attrs"
            class="zm-select__input"
            autocomplete="off"
            :aria-required="isRequired"
            :class="[selectSize ? `is-${ selectSize }` : '']"
            :disabled="selectDisabled"
            :aria-disabled="selectDisabled"
            @compositionstart="handleComposition"
            @compositionupdate="handleComposition"
            @compositionend="handleComposition"
            @keydown="handleKeyDown"
            v-model="query"
            @input="debouncedQueryChange"
            :aria-label="label"
            :aria-describedby="labelBy"
            :aria-activedescendant="visible ? activedescendant: null"
            :aria-controls="listId"
            :aria-owns="listId"
            role="combobox"
            :aria-expanded="(visible).toString()"
            aria-haspopup="true"
            :style="{ 'flex-grow': '1', width: inputLength / (inputWidth - 32) + '%', 'max-width': inputWidth - 42 + 'px' }"
            @focus="handleFocus"
            @blur="handleBlur"
            :placeholder="currentPlaceholder"
          >
        </div>
        <button
          class="zm-select-clear"
          v-show="showClear"
          type="button"
          :style="closeIconStyl"
          @click.stop="clearSelect"
          @keydonw.enter="clearSelect"
          aria-label="Clear Select"
        >
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-error','is-show-close']"></i>
        </button>
        <button
          tabindex="-1"
          class="zm-select-toggle"
          :style="upIconStyl"
          type="button"
          aria-hidden="true"
          @click.stop="handleIconClick"
          :aria-label="toggleText">
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-' + iconClass]"></i>
        </button>
      </div>
    </zm-scrollbar>
    <transition
      name="zm-zoom-in-top"
      @after-enter="handleMenuEnter"
      @after-leave="handleMenuLeave"
      :duration="duration"
    >
      <zm-select-dropdown
        ref="popper"
        :class="{'zm-select-menu-width-limit': widthLimit}"
        :append-to-body="popperAppendToBody"
        :width-limit="widthLimit"
        :popper-options="popperOptions"
        v-show="visible && emptyText!==false">
        <zm-scrollbar
          wrap-class="zm-select-dropdown__wrap"
          :wrap-style="wrapStyl"
          ref="scrollbar"
          :class="{ 'is-empty': hasEmptyCls }"
          @scroll-end="onScrollEnd"
        >
          <dl
            role="listbox"
            tabindex="-1"
            :aria-label="listLabel"
            :aria-labelledby="listLabelBy"
            :id="listId"
            class="zm-select-dropdown__list"
            v-show="shouldShowOptions"
            @keydown="handleListKeyDown"
          >
            <zm-option
              :value="query"
              created
              :option-id="query"
              :width-limit="widthLimit"
              v-if="showNewOption">
            </zm-option>
            <slot></slot>
          </dl>
          <div v-if="loading" v-loading="loading" class="zm-select__loading">
          </div>
        </zm-scrollbar>
        <template v-if="emptyText && (!allowCreate || (allowCreate && options.length === 0 ))">
          <slot name="empty" v-if="$slots.empty"></slot>
          <p class="zm-select-dropdown__empty" v-else>
            {{ emptyText }}
          </p>
        </template>
      </zm-select-dropdown>
    </transition>
  </div>
</template>

<script>
  import SelectMixin from './select-mixin';
  import { isKorean } from '../../utils/shared';
  import { valueEquals } from '../../utils/util';
  import { readAria } from '../../utils/aria';
  import debounce from 'throttle-debounce/debounce';
  import AriaUtils from '../../utils/aria-utils';

  export default {
    name: 'filterable-select',
    componentName: 'filterable-select',
    mixins: [SelectMixin],
    props: {
      loading: Boolean,
      hasMore: Boolean,
      remote: Boolean,
      remoteMethod: Function,
      filterMethod: Function,
      limitQueryLength: {
        type: Number,
        default: 0
      },
      allowCreate: Boolean,
      noMatchText: String,
      noDataText: String,
      queryWhenFocus: Boolean,
      queryWhenVisible: Boolean,
      queryAfterValueChange: Boolean,
      clearQueryWhenVisible: Boolean,
      selectedAfterFocus: Boolean,
      defaultFirstOption: Boolean
    },
    data() {
      return {
        query: '',
        previousQuery: null,
        inputWidth: 0,
        inputLength: 20,
        firstQuery: true,
        page: 1,
        visibleByQuery: false,
        filteredOptionsCount: 0,
        createdSelected: false,
        createdLabel: null,
        isOnComposition: false
      };
    },
    computed: {
      debounce() {
        return this.remote ? 300 : 0;
      },
      hasEmptyCls() {
        if (this.allowCreate || this.loading) {
          return false;
        }
        return this.filteredOptionsCount === 0;
      },
      emptyText() {
        if (this.loading) {
          return '';
        }
        if (this.remote && this.query === '' && this.options.length === 0) {
          return false;
        }
        if (this.options.length > 0 && this.filteredOptionsCount === 0) {
          return this.noMatchText || this.t('select.noMatch');
        }
        if (this.options.length === 0) {
          return this.noDataText || this.t('select.noData');
        }
        return null;
      },
      iconClass() {
        return this.remote ? '' : 'up';
      },
      closeIconStyl() {
        return this.remote ? 'right: 8px' : '';
      },
      upIconStyl() {
        return this.remote ? 'display: none' : '';
      },
      showNewOption() {
        const query = typeof this.query === 'string' ? this.query.trim() : String(this.query);
        const hasExistingOption = this.options.filter(option => !option.created).some(option => option.currentLabel === query);
        return this.allowCreate && query !== '' && !hasExistingOption;
      }
    },
    watch: {
      value(newVal, oldVal) {
        if (this.multiple) {
          if (newVal.length > 0) {
            this.currentPlaceholder = '';
          } else {
            this.currentPlaceholder = this.cachedPlaceHolder;
          }
          this.checkListVal = newVal;
          this.setSelected();
          this.$nextTick(() => {
            this.query = '';
            if (this.queryAfterValueChange) {
              this.handleQueryChange(this.query);
            }
          });
        } else {
          this.setSelected();
          this.query = this.selectedLabel;
          this.inputLength = 20;
        }
        if (!valueEquals(newVal, oldVal)) {
          this.dispatch('ZmFormItem', 'el.form.change', newVal);
        }
      },
      visible(newVal) {
        this.resetHoverIndex();
        if (newVal) {
          this.handleIconShow();
          this.broadcast('ZmSelectDropdown', 'updatePopper');
          if (!this.multiple && this.selected) {
            this.selectedLabel = this.selected.currentLabel;
          }
          if (this.clearQueryWhenVisible && !this.visibleByQuery) {
            this.query = '';
          }
          if (this.queryWhenVisible) {
            this.handleQueryChange(this.query);
          }
          if (!this.remote) {
            if (this.selectedAfterFocus && !this.clearQueryWhenVisible) {
              const targetOptions = this.options.filter(option => option.currentValue === this.value);
              if (targetOptions.length) {
                this.query = targetOptions[0].currentLabel;
              }
            }
          }
          this.isInputFocus = true;
          this.$nextTick(() => {
            this.focus();
            if (this.selectedAfterFocus) {
              (this.$refs.input || this.$refs.referenceInner).select();
            }
          });
        } else {
          this.handleIconHide();
          this.broadcast('ZmSelectDropdown', 'destroyPopper');
          this.inputLength = 20;
          this.query = '';
          this.previousQuery = null;
          this.visibleByQuery = false;

          if (!this.multiple) {
            if (this.selected) {
              if (this.allowCreate &&
                this.createdSelected && this.createdLabel) {
                this.selectedLabel = this.createdLabel;
              } else {
                this.selectedLabel = this.selected.currentLabel;
              }
              this.query = this.selectedLabel;
            }
            this.currentPlaceholder = this.cachedPlaceHolder;
          }

          this.$emit('blur');
          this.dispatch('ZmFormItem', 'el.form.blur');

          this.$nextTick(() => {
            if (this.$refs.input &&
              this.$refs.input.value === '' &&
              this.selected.length === 0) {
              this.currentPlaceholder = this.cachedPlaceHolder;
            }
          });
        }
        this.$emit('visible-change', newVal);
      },
      options() {
        if (this.selectOptions && this.sortingOptions) {
          return;
        }
        this.resetHoverIndex();
        this.setSelected();
        if (!this.visible && !this.multiple && this.selectedLabel) {
          this.query = this.selectedLabel;
        }
        if (this.defaultFirstOption && this.filteredOptionsCount) {
          this.checkDefaultFirstOption();
        }
        this.$nextTick(() => {
          this.broadcast('ZmSelectDropdown', 'updatePopper');
        });
      },
      emptyText(text) {
        if (text && !this.loading) {
          this.$nextTick(() => {
            if (this.emptyText) {
              readAria(this.emptyText);
            }
          });
        }
      }
    },
    created() {
      this.debouncedQueryChange = debounce(this.debounce, (e) => {
        this.handleQueryChange(e.target.value, true);
      });
    },
    mounted() {
      if (!this.multiple) {
        this.query = this.selectedLabel;
      }
    },
    methods: {
      focus() {
        const input = this.$refs.input || this.$refs.referenceInner;
        if (input) {
          input.focus();
        }
      },
      blur() {
        const input = this.$refs.input || this.$refs.referenceInner;
        if (input) {
          input.blur();
        }
      },
      resetInputHeight() {
        if (!this.multiple) {
          return;
        }
        this.$nextTick(() => {
          if (this.visible) {
            this.broadcast('ZmSelectDropdown', 'updatePopper');
          }
        });
      },
      handleFocus(event) {
        this.isInputFocus = true;
        this.$emit('focus', event);
        this.$nextTick(() => {
          if (!this.visible && this.queryWhenFocus) {
            this.handleQueryChange(this.query, true);
          }
        });
      },
      handleComposition(event) {
        const text = event.target.value;
        if (event.type === 'compositionend') {
          this.isOnComposition = false;
          this.handleQueryChange(text);
        } else {
          const lastCharacter = text[text.length - 1] || '';
          this.isOnComposition = !isKorean(lastCharacter);
        }
      },
      handleQueryChange(val, userInput) {
        this.firstQuery = false;
        this.page = 1;
        if (userInput && !this.visible && val) {
          this.visibleByQuery = true;
          this.visible = true;
        }
        if (this.remote && typeof this.remoteMethod === 'function') {
          if (this.query.length < this.limitQueryLength) {
            return;
          }
        }
        if (this.previousQuery === val || this.isOnComposition) {
          return;
        }
        this.previousQuery = val;
        this.$nextTick(() => {
          if (this.visible) this.broadcast('ZmSelectDropdown', 'updatePopper');
        });
        this.hoverIndex = -1;
        if (this.multiple) {
          const length = this.$refs.input.value.length * 15 + 20;
          this.inputLength = this.collapseTags ? Math.min(50, length) : length;
          this.managePlaceholder();
          // this.resetInputHeight();
        }
        if (this.remote && typeof this.remoteMethod === 'function') {
          this.hoverIndex = -1;
          this.remoteMethod(val, this.page);
        } else if (typeof this.filterMethod === 'function') {
          this.filterMethod(val);
          this.broadcast('ZmOptionGroup', 'queryChange');
        } else {
          this.filteredOptionsCount = this.optionsCount;
          this.broadcast('ZmOption', 'queryChange', val);
          this.broadcast('ZmOptionGroup', 'queryChange');
        }
        if (this.defaultFirstOption && this.filteredOptionsCount) {
          this.checkDefaultFirstOption();
        }
      },
      managePlaceholder() {
        if (this.currentPlaceholder !== '') {
          this.currentPlaceholder = this.$refs.input.value ? '' : this.cachedPlaceHolder;
        }
      },
      onScrollEnd() {
        if (!this.loading && this.hasMore) {
          this.page++;
          const remoteMethod = this.remoteMethod;
          if (typeof remoteMethod === 'function') {
            remoteMethod(this.query, this.page);
          }
        }
      },
      setSelected() {
        if (this.multiple) {
          const result = [];
          if (Array.isArray(this.value)) {
            this.value.forEach(value => {
              const val = this.getOption(value);
              result.push(val);
            });
          }
          this.selected = result;
          this.resetInputHeight();
          return;
        }
        const option = this.getOption(this.value);
        if (option.created) {
          this.createdLabel = option.currentLabel;
          this.createdSelected = true;
        } else {
          this.createdSelected = false;
        }
        this.selectedLabel = typeof this.filterLabel === 'function' ? this.filterLabel(option.currentLabel, this.value) : option.currentLabel;
        this.selected = option;
      },
      deletePrevTag(event) {
        if (event.target.value.length <= 0) {
          const value = this.value.slice();
          value.pop();
          this.$emit('input', value);
          this.emitChange(value);
        }
      },
      handleKeyDown(event) {
        if (this.selectDisabled) {
          return;
        }
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.enter:
            this.selectOption();
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.esc:
            this.handleEsc();
            event.preventDefault();
            if (this.visible) {
              event.stopPropagation();
            }
            break;
          case keys.down:
            this.navigateOptions('next');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.up:
            this.navigateOptions('prev');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.home:
            this.navigateOptions('home');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.end:
            this.navigateOptions('end');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.tab:
            this.handleTab(event);
            break;
          case keys.backspace:
            if (this.multiple) {
              this.deletePrevTag(event);
            }
            break;
        }
      },
      handleListKeyDown(event) {
        if (this.selectDisabled) {
          return;
        }
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.esc:
            this.handleEsc();
            event.preventDefault();
            event.stopPropagation();
            break;
        }
      }
    }
  };
</script>

